import type { EventCategory } from "@/types";
import { cn } from "@shared/utils";
import { Button } from "../atoms";

interface CategorySelectorProps {
  value: EventCategory[];
  onChange: (categories: EventCategory[]) => void;
  onClear: () => void;
  onApply: () => void;
  showActions?: boolean;
  showTitle?: boolean;
}

const CATEGORIES: { label: string; value: EventCategory }[] = [
  { label: "Conciertos", value: "conciertos" },
  { label: "Deportes", value: "deportes" },
  { label: "Fiestas", value: "fiestas" },
  { label: "Stand-up", value: "stand-up" },
  { label: "Otros", value: "otros" },
];

/**
 * Category selector component.
 * Displays categories as selectable rectangles with toggles.
 */
export function CategorySelector({
  value,
  onChange,
  onClear,
  onApply,
  showActions = true,
  showTitle = true,
}: CategorySelectorProps) {
  const handleCategoryToggle = (category: EventCategory) => {
    if (value.includes(category)) {
      onChange(value.filter((c) => c !== category));
    } else {
      onChange([...value, category]);
    }
  };

  return (
    <div className="space-y-3 ">
      {!showTitle && (
        <h3 className="text-primary-110 font-semibold mb-4">Categoría</h3>
      )}
      {/* Categories grid */}
      <div className="flex flex-wrap justify-start gap-2 items-center">
        {/* "Todos" — active when no category is selected */}
        <button
          onClick={() => onChange([])}
          className={cn(
            "py-2 px-4 border border-neutral-70 rounded-lg text-center text-[12px] transition-colors",
            value.length === 0
              ? "bg-secondary-120 text-primary-110 border-secondary-120"
              : "bg-white text-black hover:bg-neutral-10",
          )}
        >
          Todos
        </button>

        {CATEGORIES.map((cat) => {
          const isSelected = value.includes(cat.value);
          return (
            <button
              key={cat.value}
              onClick={() => handleCategoryToggle(cat.value)}
              className={cn(
                "py-2 px-4 border border-neutral-70 rounded-lg text-center text-[12px] transition-colors",
                isSelected
                  ? "bg-secondary-120 text-primary-110 border-secondary-120"
                  : "bg-white text-black hover:bg-neutral-10",
              )}
            >
              {cat.label}
            </button>
          );
        })}
      </div>

      {/* Action buttons */}
      {showActions && (
        <div className="flex gap-3 pt-4">
          <Button
            className="w-[135px] border border-primary-110 bg-white text-primary-110 hover:bg-primary-10"
            onClick={() => {
              onChange([]);
              onClear();
            }}
          >
            Limpiar
          </Button>

          <Button
            className="w-[135px] border border-primary-110 bg-secondary-120 hover:bg-secondary-120/90"
            onClick={onApply}
          >
            Aplicar
          </Button>
        </div>
      )}
    </div>
  );
}
